import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Alerts from "@/pages/Alerts";
import History from "@/pages/History";
import Users from "@/pages/Users";
import TakeOut from "@/pages/TakeOut";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Appointments from "@/pages/Appointments";
import ClinicReports from "@/pages/ClinicReports";
import Portal from "@/pages/Portal";
import WebsiteAdmin from "@/pages/WebsiteAdmin";
import SiteLayout from "@/site/SiteLayout";
import Home from "@/site/pages/Home";
import About from "@/site/pages/About";
import Services from "@/site/pages/Services";
import MedicalTourism from "@/site/pages/MedicalTourism";
import TestimonialsPage from "@/site/pages/Testimonials";
import Contact from "@/site/pages/Contact";
import GetQuote from "@/site/pages/GetQuote";
import TrackOrder from "@/site/pages/TrackOrder";
import Photos from "@/site/pages/Photos";
import Videos from "@/site/pages/Videos";
import Downloads from "@/site/pages/Downloads";
import BionicProstheses from "@/site/pages/BionicProstheses";
import ThemeInjector from "@/components/ThemeInjector";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function fallbackFor(user) {
  if (user?.role === "patient") return "/clinic/portal";
  if (user?.permissions?.includes("appointment:read")) return "/clinic/appointments";
  return "/inventory";
}

function RequirePerm({ perm, children }) {
  const { user } = useAuth();
  if (!user?.permissions?.includes(perm)) return <Navigate to={fallbackFor(user)} replace />;
  return children;
}

function ClinicHome() {
  const { user } = useAuth();
  return <Navigate to={fallbackFor(user)} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Public marketing website */}
      <Route element={<SiteLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="services" element={<Services />} />
        <Route path="medical-tourism" element={<MedicalTourism />} />
        <Route path="testimonials" element={<TestimonialsPage />} />
        <Route path="contact" element={<Contact />} />
        <Route path="get-a-quote" element={<GetQuote />} />
        <Route path="track-order" element={<TrackOrder />} />
        <Route path="photos" element={<Photos />} />
        <Route path="videos" element={<Videos />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="ai-bionic" element={<BionicProstheses />} />
      </Route>

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* CLINIC — reached from the website login */}
      <Route element={<Protected><Layout variant="clinic" /></Protected>}>
        <Route path="clinic" element={<ClinicHome />} />
        <Route path="clinic/portal" element={<RequirePerm perm="portal:read"><Portal /></RequirePerm>} />
        <Route path="clinic/patients" element={<RequirePerm perm="patient:read"><Patients /></RequirePerm>} />
        <Route path="clinic/patients/:id" element={<RequirePerm perm="patient:read"><PatientDetail /></RequirePerm>} />
        <Route path="clinic/appointments" element={<RequirePerm perm="appointment:read"><Appointments /></RequirePerm>} />
        <Route path="clinic/reports" element={<RequirePerm perm="report:read"><ClinicReports /></RequirePerm>} />
      </Route>

      {/* INVENTORY — its own separate section (raphadon.store/inventory) */}
      <Route path="inventory" element={<Protected><Layout variant="inventory" /></Protected>}>
        <Route index element={<RequirePerm perm="transaction:create"><Dashboard /></RequirePerm>} />
        <Route path="items" element={<RequirePerm perm="transaction:create"><Inventory /></RequirePerm>} />
        <Route path="take-out" element={<RequirePerm perm="transaction:create"><TakeOut /></RequirePerm>} />
        <Route path="alerts" element={<RequirePerm perm="transaction:create"><Alerts /></RequirePerm>} />
        <Route path="history" element={<RequirePerm perm="transaction:create"><History /></RequirePerm>} />
        <Route path="website" element={<RequirePerm perm="settings:manage"><WebsiteAdmin /></RequirePerm>} />
        <Route path="users" element={<RequirePerm perm="user:manage"><Users /></RequirePerm>} />
      </Route>
      {/* Legacy paths -> /inventory */}
      <Route path="/dashboard" element={<Navigate to="/inventory" replace />} />
      <Route path="/store" element={<Navigate to="/inventory" replace />} />
      <Route path="/store/*" element={<Navigate to="/inventory" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ThemeInjector />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
